import axios from 'axios';
const { v4: uuidv4 } = require('uuid');
 
// Replace with your API key and endpoint
const apiKey = '28DQexAly0JN4YaeB2Qkd1OTElhO4Cx7PZ5WZj3XPtJTBUhu1hQ4JQQJ99BGAC5T7U2XJ3w3AAAbACOGNUxZ';
const endpoint = 'https://api.cognitive.microsofttranslator.com/';
 
// Location required for regional resources (replace with your resource location)
const location = 'francecentral';

// Rate limiting to prevent 429 errors
const RATE_LIMIT_DELAY = 100; // ms between actual API requests
let lastRequestTime = 0;
const requestQueue = [];
let isProcessingQueue = false;

// Translation cache — loaded from localStorage so it persists across reloads
const CACHE_KEY_PREFIX = 'ev_trans__';
const CACHE_EXPIRY = 7 * 24 * 60 * 60 * 1000; // 7 days

const translationCache = new Map();

// Load previously saved translations from localStorage into memory
(() => {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_KEY_PREFIX)) {
        const entry = JSON.parse(localStorage.getItem(key));
        if (entry && Date.now() - entry.timestamp < CACHE_EXPIRY) {
          translationCache.set(key.slice(CACHE_KEY_PREFIX.length), entry);
        } else {
          localStorage.removeItem(key);
        }
      }
    }
  } catch (e) { /* localStorage may be unavailable in some environments */ }
})();

const getCacheKey = (text, targetLang) => `${text}__${targetLang}`;
export { translationCache, getCacheKey, CACHE_EXPIRY };

const rateLimitedRequest = async (requestFn) => {
  return new Promise((resolve, reject) => {
    requestQueue.push({ requestFn, resolve, reject });
    processQueue();
  });
};

const processQueue = async () => {
  if (isProcessingQueue || requestQueue.length === 0) return;
  
  isProcessingQueue = true;
  
  while (requestQueue.length > 0) {
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTime;
    
    if (timeSinceLastRequest < RATE_LIMIT_DELAY) {
      await new Promise(resolve => setTimeout(resolve, RATE_LIMIT_DELAY - timeSinceLastRequest));
    }
    
    const { requestFn, resolve, reject } = requestQueue.shift();
    lastRequestTime = Date.now();
    
    try {
      const result = await requestFn();
      resolve(result);
    } catch (error) {
      reject(error);
    }
  }
  
  isProcessingQueue = false;
};
 
// Set up the headers for authentication
const headers = {
  'Ocp-Apim-Subscription-Key': apiKey,
  'Content-Type': 'application/json',
  'Ocp-Apim-Subscription-Region': location, // e.g. 'eastus'
  'X-ClientTraceId': uuidv4().toString(),
};
 
// Function to detect language
export const detectLanguage = async (text) => {
  try {
    const response = await rateLimitedRequest(() =>
      axios.post(
        `${endpoint}detect?api-version=3.0`,
        [{ Text: text }],
        { headers }
      )
    );
    return response.data[0].language; // Returns the detected language code
  } catch (error) {
    console.error('Error detecting language:', error?.response?.status === 429 ? 'Rate limit exceeded' : error.message);
    return null;
  }
};
 
// Function to translate text with caching and rate limiting
export const translateText = async (text, targetLanguage) => {
  if (!text || !targetLanguage) return text;
  
  // Check in-memory cache first — no queue, instant return
  const cacheKey = getCacheKey(text, targetLanguage);
  const cached = translationCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_EXPIRY) {
    return cached.value;
  }
  
  try {
    const response = await rateLimitedRequest(() =>
      axios.post(
        `${endpoint}translate?api-version=3.0&to=${targetLanguage}`,
        [{ Text: text }],
        { headers: { ...headers, 'X-ClientTraceId': uuidv4().toString() } }
      )
    );
    const translated = response.data[0].translations[0].text;
    
    // Cache in memory
    const entry = { value: translated, timestamp: Date.now() };
    translationCache.set(cacheKey, entry);
    // Persist to localStorage so next page load is instant
    try {
      localStorage.setItem(CACHE_KEY_PREFIX + cacheKey, JSON.stringify(entry));
    } catch (e) { /* quota exceeded or unavailable */ }
    
    return translated;
  } catch (error) {
    if (error?.response?.status === 429) {
      console.warn('Translation rate limit exceeded, returning original text');
    } else {
      console.error('Error translating text:', error);
    }
    return text;
  }
};